/* AdminCatalogImports — admin command center for the multi-source
 * catalog import system. Lets admins:
 *   - browse every import job across all sellers
 *   - inspect job detail (preview rows + per-row errors)
 *   - retry failed jobs
 *   - discard junk jobs from the audit log
 *   - flip connector enable/disable + patch credentials
 *   - trigger a manual sync pass
 *   - see top-line KPIs (jobs/day, error rate)
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  RefreshCcw, Play, AlertCircle, CheckCircle2, Eye, X,
  Database, ShoppingBag, Cloud, AtSign, Globe, FileText,
  FileSpreadsheet, Clock, Trash2,
} from "lucide-react";

const KIND_ICON = {
  csv: FileSpreadsheet, xlsx: FileSpreadsheet, pdf: FileText, image: FileText,
  shopify: ShoppingBag, gsheet: Cloud, email: AtSign, webhook: Globe, ftp: Database,
};

export default function AdminCatalogImports() {
  const [jobs, setJobs] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [health, setHealth] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [j, c, h] = await Promise.all([
        api.get("/admin/catalog-import/jobs", { params: { status: filterStatus || undefined, source_kind: filterSource || undefined } }),
        api.get("/admin/catalog-import/connectors"),
        api.get("/admin/catalog-import/health"),
      ]);
      setJobs(j.data?.jobs || []);
      setConnectors(c.data?.connectors || []);
      setHealth(h.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load");
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterStatus, filterSource]);

  const runSyncNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/catalog-import/sync-now");
      toast.success(`Sync pass complete — ran ${r.data.ran} · failed ${r.data.failed}`);
      refresh();
    } catch (e) { toast.error("Sync failed"); }
    finally { setBusy(false); }
  };

  const retryJob = async (jid) => {
    try {
      const r = await api.post(`/admin/catalog-import/jobs/${jid}/retry`);
      toast.success(`Retried — ${r.data.created || 0} new, ${r.data.updated || 0} updated`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
  };
  const discardJob = async (jid) => {
    if (!window.confirm("Discard this job from the audit log?")) return;
    try { await api.post(`/admin/catalog-import/jobs/${jid}/discard`); refresh(); }
    catch { toast.error("Discard failed"); }
  };
  const toggleConnector = async (c) => {
    try { await api.put(`/admin/catalog-import/connectors/${c.id}`, { enabled: !c.enabled }); refresh(); }
    catch { toast.error("Toggle failed"); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="admin-catalog-imports">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Admin · Catalog</div>
          <h1 className="display text-3xl mt-1">Catalog imports</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refresh} className="rounded-full"><RefreshCcw className="w-3.5 h-3.5 mr-1.5"/>Refresh</Button>
          <Button onClick={runSyncNow} disabled={busy} data-testid="admin-sync-now"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            <Play className="w-3.5 h-3.5 mr-1.5"/>Run sync now
          </Button>
        </div>
      </div>

      {/* HEALTH KPIs */}
      {health && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-kpis">
          <Kpi label={`Jobs · ${health.window_hours}h`} value={health.total_jobs}/>
          <Kpi label="Rows applied" value={(health.rows_applied || 0).toLocaleString()}/>
          <Kpi label="Error rate" value={`${((health.error_rate || 0) * 100).toFixed(1)}%`} tone={health.error_rate > 0.1 ? "rose" : "default"}/>
          <Kpi label="Connectors enabled" value={`${health.connectors_enabled} / ${health.connectors_total}`}/>
          <Kpi label="Failed (24h)" value={health.failed} tone={health.failed > 0 ? "amber" : "default"}/>
        </div>
      )}

      {/* CONNECTORS */}
      <section data-testid="admin-connectors">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm mono uppercase tracking-[0.18em] text-foreground/55">Connectors</h2>
          <div className="text-[11px] text-foreground/55">{connectors.length} total</div>
        </div>
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          {connectors.length === 0 ? (
            <div className="p-10 text-center text-foreground/45 text-sm">No connectors set up yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 border-b border-border">
                <tr><Th>Seller</Th><Th>Kind</Th><Th>Name</Th><Th>Schedule</Th><Th>Last run</Th><Th>Status</Th><Th right>Actions</Th></tr>
              </thead>
              <tbody>
                {connectors.map(c => {
                  const Icon = KIND_ICON[c.kind] || Database;
                  return (
                    <tr key={c.id} className="border-b last:border-b-0 border-border hover:bg-secondary/30">
                      <Td><span className="mono text-[11px] text-foreground/65">{c.seller_id.slice(0, 8)}…</span></Td>
                      <Td><span className="chip bg-secondary text-[10px] inline-flex items-center gap-1"><Icon className="w-3 h-3"/>{c.kind}</span></Td>
                      <Td>{c.name}</Td>
                      <Td><span className="text-[11px] mono uppercase tracking-wider text-foreground/55">{c.schedule}</span></Td>
                      <Td><span className="text-[11px] text-foreground/55">{c.last_run_at ? new Date(c.last_run_at).toLocaleString() : "never"}</span></Td>
                      <Td>{c.enabled
                        ? <span className="chip bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px]"><CheckCircle2 className="w-2.5 h-2.5"/>Enabled</span>
                        : <span className="chip bg-secondary text-[10px]">Paused</span>}
                      </Td>
                      <Td right>
                        <Button size="sm" variant="outline" onClick={() => toggleConnector(c)} className="rounded-full h-8 px-3 text-xs">
                          {c.enabled ? "Pause" : "Enable"}
                        </Button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* JOB LOG */}
      <section data-testid="admin-jobs">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="text-sm mono uppercase tracking-[0.18em] text-foreground/55">Import jobs</h2>
          <div className="flex gap-2">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="h-9 rounded-md border border-border bg-white px-3 text-xs">
              <option value="">All statuses</option>
              <option value="applied">Applied</option>
              <option value="preview">Preview</option>
              <option value="failed">Failed</option>
              <option value="discarded">Discarded</option>
            </select>
            <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
              className="h-9 rounded-md border border-border bg-white px-3 text-xs">
              <option value="">All sources</option>
              <option value="csv">CSV</option><option value="xlsx">Excel</option><option value="pdf">PDF</option>
              <option value="shopify">Shopify</option><option value="gsheet">Google Sheet</option>
              <option value="email">Email</option><option value="webhook">Webhook</option><option value="ftp">FTP</option>
            </select>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 border-b border-border">
              <tr><Th>When</Th><Th>Seller</Th><Th>Source</Th><Th>File / Profile</Th><Th right>New</Th><Th right>Updated</Th><Th right>Skipped</Th><Th right>Failed</Th><Th>Status</Th><Th right>Actions</Th></tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-b last:border-b-0 border-border hover:bg-secondary/30">
                  <Td><span className="mono text-[11px] text-foreground/65">{new Date(j.created_at).toLocaleString()}</span></Td>
                  <Td><span className="mono text-[11px]">{j.seller_id?.slice(0, 8)}…</span></Td>
                  <Td><span className="chip bg-secondary text-[10px] uppercase mono">{j.source_kind}</span></Td>
                  <Td><div className="truncate max-w-xs">{j.filename || j.profile_name || "—"}</div></Td>
                  <Td right><span className="mono text-emerald-700">{j.rows_created}</span></Td>
                  <Td right><span className="mono text-blue-700">{j.rows_updated}</span></Td>
                  <Td right><span className="mono text-foreground/55">{j.rows_skipped}</span></Td>
                  <Td right><span className={`mono ${j.rows_failed > 0 ? "text-rose-600" : "text-foreground/45"}`}>{j.rows_failed}</span></Td>
                  <Td><StatusChip status={j.status}/></Td>
                  <Td right>
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setDetail(j.id)} className="rounded-md h-7 px-2 text-[11px]" title="View"><Eye className="w-3 h-3"/></Button>
                      {(j.status === "failed" || j.status === "applied") && (
                        <Button size="sm" variant="outline" onClick={() => retryJob(j.id)} className="rounded-md h-7 px-2 text-[11px]" title="Retry">
                          <RefreshCcw className="w-3 h-3"/>
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => discardJob(j.id)} className="rounded-md h-7 px-2 text-[11px] text-rose-600" title="Discard">
                        <Trash2 className="w-3 h-3"/>
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr><td colSpan={10} className="p-10 text-center text-foreground/45 text-sm">No jobs match these filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {detail && <JobDetailModal jid={detail} onClose={() => setDetail(null)}/>}
    </div>
  );
}

function JobDetailModal({ jid, onClose }) {
  const [job, setJob] = useState(null);
  useEffect(() => { api.get(`/admin/catalog-import/jobs/${jid}`).then(r => setJob(r.data)).catch(() => onClose()); }, [jid, onClose]);
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="admin-job-detail">
        <DialogHeader><DialogTitle>Job detail</DialogTitle></DialogHeader>
        {!job ? <div className="py-10 text-center text-foreground/45">Loading…</div> : (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <Stat l="Status" v={<StatusChip status={job.status}/>}/>
              <Stat l="Created" v={new Date(job.created_at).toLocaleString()}/>
              <Stat l="Completed" v={job.completed_at ? new Date(job.completed_at).toLocaleString() : "—"}/>
              <Stat l="Source" v={job.source_kind}/>
              <Stat l="File" v={job.filename || job.profile_name || "—"}/>
              <Stat l="Used AI" v={job.meta?.used_ai ? "Yes" : "No"}/>
              <Stat l="Rows" v={`${job.rows_total} total · ${job.rows_created} new · ${job.rows_updated} updated`}/>
              <Stat l="Skipped" v={job.rows_skipped}/>
              <Stat l="Failed" v={job.rows_failed}/>
            </div>
            {job.error && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800">
                <AlertCircle className="inline w-3.5 h-3.5 mr-1"/><strong>Error:</strong> {job.error}
              </div>
            )}
            {(job.detected_columns?.length > 0) && (
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55 mb-1.5">Detected columns</div>
                <div className="flex flex-wrap gap-1">
                  {job.detected_columns.map(c => <span key={c} className="chip bg-secondary text-[10px]">{c}</span>)}
                </div>
              </div>
            )}
            {(job.row_errors?.length > 0) && (
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55 mb-1.5">Per-row errors ({job.row_errors.length})</div>
                <div className="rounded-xl border border-border overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-secondary"><tr><th className="text-left px-2 py-1.5">Row</th><th className="text-left px-2 py-1.5">SKU</th><th className="text-left px-2 py-1.5">Error</th></tr></thead>
                    <tbody>
                      {job.row_errors.slice(0, 100).map((e, i) => (
                        <tr key={i} className="border-t border-border"><td className="px-2 py-1.5 mono">{e.row_index ?? "—"}</td><td className="px-2 py-1.5 mono">{e.sku || "—"}</td><td className="px-2 py-1.5">{e.reason || e.error}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {(job.preview_rows?.length > 0) && (
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55 mb-1.5">Preview rows ({job.preview_rows.length})</div>
                <pre className="rounded-xl bg-secondary p-3 overflow-auto max-h-64 text-[10.5px] mono">{JSON.stringify(job.preview_rows.slice(0, 20), null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, tone }) {
  const toneCls = tone === "rose" ? "border-rose-200 bg-rose-50" : tone === "amber" ? "border-amber-200 bg-amber-50" : "border-border bg-white";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`}>
      <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">{label}</div>
      <div className="display text-2xl mt-1">{value}</div>
    </div>
  );
}
function Stat({ l, v }) {
  return <div><div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">{l}</div><div className="text-sm mt-0.5">{v}</div></div>;
}
function Th({ children, right }) {
  return <th style={{ textAlign: right ? "right" : "left" }} className="text-[10px] uppercase tracking-[0.18em] mono font-medium py-2.5 px-3 text-foreground/55">{children}</th>;
}
function Td({ children, right }) {
  return <td style={{ textAlign: right ? "right" : "left", verticalAlign: "middle" }} className="py-2.5 px-3">{children}</td>;
}
function StatusChip({ status }) {
  const map = {
    applied:   { l: "Applied",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    preview:   { l: "Preview",  cls: "bg-amber-50 text-amber-700 border-amber-200" },
    extracting:{ l: "Working",  cls: "bg-blue-50 text-blue-700 border-blue-200" },
    failed:    { l: "Failed",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
    discarded: { l: "Discarded",cls: "bg-secondary text-foreground/55 border-border" },
    pending:   { l: "Pending",  cls: "bg-secondary text-foreground/55 border-border" },
  };
  const m = map[status] || { l: status, cls: "bg-secondary border-border" };
  return <span className={`chip border text-[10px] ${m.cls}`}>{m.l}</span>;
}
